'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { useApp } from '@/lib/AppContext';
import { getUserProgramSelection } from '@/lib/programManagement';
import { getCachedProgram } from '@/lib/programCache';
import { generateUserWorkout, type GeneratedExercise } from '@/lib/workoutGenerator';
import type { UserProgramSelection } from '@/lib/types/program';

interface WeekScheduleCardProps {
  currentWeek: number;
  currentDay: number;
  programSelection: UserProgramSelection;
}

interface DaySummary {
  day: number;
  loading: boolean;
  isRest: boolean;
  /** training = real session, active-rest = mobility/recovery work,
   *  passive-rest = full day off. */
  dayType: 'training' | 'active-rest' | 'passive-rest';
  sessionType: string;
  exercises: string[]; // exercise names (mobility moves for active-rest)
  exerciseCount: number;
}

function isPrepOrRecovery(blockName: string): boolean {
  const lower = blockName.toLowerCase();
  return lower.includes('prep') || lower.includes('recovery') ||
    lower.includes('warm') || lower.includes('cool') || lower.includes('activation');
}

export default function WeekScheduleCard({ currentWeek, currentDay, programSelection }: WeekScheduleCardProps) {
  const { user } = useAuth();
  const { oneRMs } = useApp();
  const [days, setDays] = useState<DaySummary[]>([]);
  const [expandedDay, setExpandedDay] = useState<number | null>(currentDay);
  const [loading, setLoading] = useState(true);
  // Today's day-of-week (1=Mon..7=Sun). Computed on the CLIENT after mount
  // so the static-export build's HTML doesn't lock in whatever weekday
  // the build server happened to run on (which would then mismatch the
  // client at hydration and throw React #300).
  const [todayDow, setTodayDow] = useState<number | null>(null);

  useEffect(() => {
    const jsDow = new Date().getDay();          // 0=Sun, 1=Mon, ...
    setTodayDow(jsDow === 0 ? 7 : jsDow);
  }, []);

  useEffect(() => {
    if (!user) return;
    loadWeek();
  }, [user, currentWeek, programSelection?.programId]);

  const loadWeek = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const selection = await getUserProgramSelection(user.uid);
      if (!selection) { setLoading(false); return; }

      const program = await getCachedProgram(selection.programId, user.uid);
      if (!program) { setLoading(false); return; }

      const summaries: DaySummary[] = [];

      for (let d = 1; d <= 7; d++) {
        try {
          const exercises = await generateUserWorkout(program, currentWeek, d, oneRMs || {});
          if (!exercises || exercises.length === 0) {
            // No generated work at all → passive rest (full day off).
            summaries.push({ day: d, loading: false, isRest: true, dayType: 'passive-rest', sessionType: 'Rest', exercises: [], exerciseCount: 0 });
            continue;
          }
          const main = exercises.filter(ex => !isPrepOrRecovery(ex.block));

          if (main.length === 0) {
            // No main work, but some movement present → active recovery
            // (mobility / recovery block). Surface the mobility moves so
            // the user knows it's a light session, not a phantom workout.
            const mobility = exercises.map(ex => ex.name);
            summaries.push({
              day: d,
              loading: false,
              isRest: true,
              dayType: 'active-rest',
              sessionType: 'Active Recovery',
              exercises: mobility,
              exerciseCount: mobility.length,
            });
          } else {
            const firstEx = main[0] as GeneratedExercise & { sessionType?: string };
            const type = firstEx.sessionType === 'assessment' ? 'Assessment' : 'Training';
            summaries.push({
              day: d,
              loading: false,
              isRest: false,
              dayType: 'training',
              sessionType: type,
              exercises: main.map(ex => ex.name),
              exerciseCount: main.length,
            });
          }
        } catch {
          summaries.push({ day: d, loading: false, isRest: true, dayType: 'passive-rest', sessionType: 'Rest', exercises: [], exerciseCount: 0 });
        }
      }

      setDays(summaries);
    } catch (err) {
      console.error('Failed to load week schedule:', err);
    } finally {
      setLoading(false);
    }
  };

  const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  if (loading) {
    return (
      <div className="space-y-1.5">
        {Array.from({ length: 7 }, (_, i) => (
          <div key={i} className="h-10 rounded-md bg-hud-ink/60 border border-slate-800/80 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {days.map((daySummary) => {
        // todayDow is null until the client mounts — server-render shows
        // no "TODAY" highlight, which matches the first client paint and
        // avoids the hydration mismatch that would throw React #300.
        const isToday = todayDow !== null && daySummary.day === todayDow;
        const isNextSession = !isToday && daySummary.day === currentDay;
        const isPast = todayDow !== null && daySummary.day < todayDow;
        const isExpanded = expandedDay === daySummary.day;

        // Severity color per state (aligns with the rest of the
        // dashboard's avionics palette: amber=today, cyan=next-session,
        // emerald=completed past, slate=future or rest).
        const accent =
          isToday ? 'amber' :
          isNextSession ? 'cyan' :
          isPast ? 'emerald' :
          'slate';
        const accentTw = {
          amber: { bar: 'bg-amber-500', text: 'text-amber-400', border: 'border-amber-500/40', bg: 'bg-amber-500/[0.04]' },
          cyan: { bar: 'bg-hud-cyan', text: 'text-hud-cyan', border: 'border-hud-cyan/40', bg: 'bg-hud-cyan/[0.04]' },
          emerald: { bar: 'bg-emerald-500/60', text: 'text-emerald-400/70', border: 'border-slate-800/80', bg: 'bg-hud-ink/40' },
          slate: { bar: 'bg-slate-700', text: 'text-slate-400', border: 'border-slate-800/80', bg: 'bg-hud-ink/40' },
        }[accent];

        return (
          <div key={daySummary.day}>
            <button
              onClick={() => setExpandedDay(isExpanded ? null : daySummary.day)}
              className={`w-full relative flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-md border ${accentTw.border} ${accentTw.bg} transition active:scale-[0.997] ${isPast && !isToday ? 'opacity-60' : ''}`}
            >
              <span aria-hidden className={`absolute top-1 bottom-1 left-0 w-0.5 ${accentTw.bar}`} />

              {/* Day label — mono, uppercase, tracked */}
              <div className={`w-10 text-left font-mono text-[10px] font-bold uppercase tracking-[0.22em] ${accentTw.text}`}>
                {DAY_LABELS[daySummary.day - 1]}
              </div>

              {/* Day indicator dot */}
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                daySummary.isRest ? 'bg-slate-700' : accentTw.bar
              }`} />

              {/* Session info */}
              <div className="flex-1 text-left min-w-0">
                {daySummary.dayType === 'passive-rest' ? (
                  <span className="font-mono text-[11px] text-slate-600 italic">Rest Day</span>
                ) : daySummary.dayType === 'active-rest' ? (
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-[12px] text-violet-300">Active Recovery</span>
                    <span className="font-mono text-[10px] text-slate-500 tabular-nums">
                      {daySummary.exerciseCount} mobility
                    </span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-2">
                    <span className={`font-mono text-[12px] ${isPast && !isToday ? 'text-slate-500' : 'text-white'}`}>
                      {daySummary.sessionType}
                    </span>
                    <span className="font-mono text-[10px] text-slate-500 tabular-nums">
                      {daySummary.exerciseCount} ex
                    </span>
                  </div>
                )}
              </div>

              {/* Status chip */}
              {isToday && (
                <span className="font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-amber-400 bg-amber-500/15 border border-amber-500/40 px-1.5 py-0.5 rounded">
                  Today
                </span>
              )}
              {!isToday && isNextSession && (
                <span className="font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-hud-cyan bg-hud-cyan/10 border border-hud-cyan/40 px-1.5 py-0.5 rounded">
                  Next
                </span>
              )}

              {/* Expand icon — training AND active-recovery days have
                  exercises worth expanding; only passive rest doesn't. */}
              {daySummary.dayType !== 'passive-rest' && (
                <div className="text-slate-600">
                  {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </div>
              )}
            </button>

            {/* Expanded exercise list (mobility moves for active recovery) */}
            {isExpanded && daySummary.dayType !== 'passive-rest' && (
              <div className="ml-[60px] mr-2 mt-1 mb-1 pl-3 border-l border-slate-800/80 space-y-0.5">
                {daySummary.exercises.map((name, idx) => (
                  <div key={idx} className="font-mono text-[11px] text-slate-500 py-0.5">
                    {name}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* History link */}
      <div className="pt-1">
        <Link
          href="/history"
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 hover:text-amber-400 transition"
        >
          View full history &rarr;
        </Link>
      </div>
    </div>
  );
}
